import { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFaceSmilePlus } from '@fortawesome/pro-regular-svg-icons';
import { reactionEmoji } from '../lib/reactionEmoji';
import { useReactionsBar } from '../lib/useReactionsBar';
import { Reaction, ReactionType } from '../model/post.types';
import { AddReactionPopover } from './AddReactionPopover';

type Props = {
  reactions: Reaction[];
  onToggle: (type: ReactionType) => Promise<unknown> | void;
};

export const PostReactions = ({ reactions, onToggle }: Props) => {
  const { visibleReactions, restCount, expanded, toggleExpanded } =
    useReactionsBar(reactions);
  const [pending, setPending] = useState<ReadonlySet<ReactionType>>(new Set());
  const addRef = useRef<HTMLButtonElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleToggle = (type: ReactionType) => {
    if (pending.has(type)) return;

    setPending((current) => new Set(current).add(type));
    Promise.resolve(onToggle(type)).finally(() =>
      setPending((current) => {
        const next = new Set(current);
        next.delete(type);
        return next;
      }),
    );
  };

  return (
    <div className="flex flex-row flex-wrap items-center gap-1.5">
      {visibleReactions.map((reaction) => (
        <button
          key={reaction.type}
          type="button"
          aria-label={`${reactionEmoji(reaction.type)} ${reaction.count}`}
          disabled={pending.has(reaction.type)}
          onClick={() => handleToggle(reaction.type)}
          className={`flex h-[26px] items-center gap-1 rounded-[6px] px-1.5 disabled:opacity-50 ${
            reaction.isToggled
              ? 'border border-[#5F1ECC] bg-[#EDE5FC] text-[#350584]'
              : 'border border-transparent bg-[#F8F8F8] text-black'
          }`}
        >
          <span className="text-[16px] leading-none">
            {reactionEmoji(reaction.type)}
          </span>
          <span className="text-[12px] leading-[1.25]">{reaction.count}</span>
        </button>
      ))}
      {restCount > 0 && (
        <button
          type="button"
          onClick={toggleExpanded}
          className="flex h-[26px] items-center rounded-[6px] bg-[#F8F8F8] px-1.5 text-[12px] text-black"
        >
          {expanded ? 'Show less' : `${restCount} more`}
        </button>
      )}
      <button
        ref={addRef}
        type="button"
        aria-label="Add reaction"
        onClick={() => setPickerOpen(true)}
        className="flex h-[26px] items-center justify-center rounded-[6px] bg-[#F8F8F8] px-2 text-base text-[#5F1ECC]"
      >
        <FontAwesomeIcon icon={faFaceSmilePlus} />
      </button>
      <AddReactionPopover
        open={pickerOpen}
        anchorEl={addRef.current}
        onClose={() => setPickerOpen(false)}
        onSelect={(type) => {
          setPickerOpen(false);
          handleToggle(type);
        }}
      />
    </div>
  );
};
